/**
 * Who can be picked as a manager, and how often. Keep in lockstep with
 * erp_api/routes/controllers/employeeManagerRules.ts.
 */

export const ACCESS_TYPE_EMPLOYEE = 'Employee';
export const ACCESS_TYPE_MANAGER = 'Manager';
export const ACCESS_TYPE_CEO = 'CEO';
/** Legacy value stored before the CEO rename — normalize on read. */
export const LEGACY_ACCESS_TYPE_CTO = 'CTO';

export const OWNER_SELF_PLACEMENT_ONLY_MESSAGE = (
  'Only the owner can assign themselves to a department or designation'
);

export const DESIGNATE_BELOW_ONLY_MESSAGE = (
  'You can only change designation for people below you in the reporting line'
);

export const normalizeEmployeeAccessType = (raw: unknown): string => {
  const value = String(raw || '').trim();
  if (value === LEGACY_ACCESS_TYPE_CTO) return ACCESS_TYPE_CEO;
  return value;
};

export const isCeoAccessType = (raw: unknown): boolean => (
  normalizeEmployeeAccessType(raw) === ACCESS_TYPE_CEO
);

export const isDevelopmentEnv = (): boolean => (
  process.env.NODE_ENV === 'development'
);

export const isOwnerEmployeeFields = (
  fields?: Record<string, string> | null,
): boolean => String(fields?.isOwner || '') === 'Yes';

/** Manager and CEO hold manage access. Owner only in development. */
export const employeeAccessActsAsManager = (
  fields?: Record<string, string> | null,
): boolean => {
  const access = normalizeEmployeeAccessType(fields?.accessType);
  if (access === ACCESS_TYPE_MANAGER || access === ACCESS_TYPE_CEO) return true;
  if (isOwnerEmployeeFields(fields) && isDevelopmentEnv()) return true;
  return false;
};

/** Form reviewers: Manager, CEO, or organization owner in any environment. */
export const employeeCanApproveForms = (
  fields?: Record<string, string> | null,
): boolean => {
  if (isOwnerEmployeeFields(fields)) return true;
  const access = normalizeEmployeeAccessType(fields?.accessType);
  return access === ACCESS_TYPE_MANAGER || access === ACCESS_TYPE_CEO;
};

export const employeeAccessTypeHoldsManageAccess = (accessType: unknown): boolean => {
  const value = normalizeEmployeeAccessType(accessType);
  return value === ACCESS_TYPE_MANAGER || value === ACCESS_TYPE_CEO;
};

type EmployeeRow = {
  id: string
  fields?: Record<string, string> | null
};

/** Manager (or CEO / Owner-in-dev) must belong to the employee's department. */
export const employeeIsManagerInDepartment = (
  managerFields: Record<string, string> | null | undefined,
  departmentId: string,
): boolean => {
  if (!employeeAccessActsAsManager(managerFields)) return false;
  const dept = String(departmentId || '').trim();
  if (!dept) return false;
  return String(managerFields?.department || '').trim() === dept;
};

/**
 * True when the caller sits on the target's manager chain (direct manager or above).
 * Never true for the person themselves.
 */
export const isCallerAboveEmployeeInReportingLine = (
  callerEmployeeId: string | null | undefined,
  targetEmployeeId: string | null | undefined,
  targetManagerId: string | null | undefined,
  managerByEmployeeId: Record<string, string> = {},
): boolean => {
  const callerId = String(callerEmployeeId || '').trim();
  const targetId = String(targetEmployeeId || '').trim();
  if (!callerId) return false;
  if (targetId && callerId === targetId) return false;

  let cursor = String(targetManagerId || managerByEmployeeId[targetId] || '').trim();
  const seen = new Set<string>();
  while (cursor) {
    if (cursor === callerId) return true;
    if (seen.has(cursor)) break;
    seen.add(cursor);
    cursor = String(managerByEmployeeId[cursor] || '').trim();
  }
  return false;
};

/**
 * Department / designation placement:
 * - Owner: only the owner may change their own department or designation (any value, any env).
 * - Everyone else: designation changes only for people below the caller in the reporting line.
 */
export const employeePlacementChangeError = ({
  callerEmployeeId,
  targetEmployeeId,
  targetIsOwner,
  targetManagerId,
  managerByEmployeeId,
  changingDepartment,
  changingDesignation,
}: {
  callerEmployeeId?: string | null
  targetEmployeeId?: string | null
  targetIsOwner: boolean
  targetManagerId?: string | null
  managerByEmployeeId?: Record<string, string>
  changingDepartment: boolean
  changingDesignation: boolean
}): string | null => {
  if (!changingDepartment && !changingDesignation) return null;
  const callerId = String(callerEmployeeId || '').trim();
  const targetId = String(targetEmployeeId || '').trim();

  if (targetIsOwner) {
    if (!callerId || !targetId || callerId !== targetId) {
      return OWNER_SELF_PLACEMENT_ONLY_MESSAGE;
    }
    return null;
  }

  if (!changingDesignation) return null;
  if (!isCallerAboveEmployeeInReportingLine(
    callerId,
    targetId,
    targetManagerId,
    managerByEmployeeId || {},
  )) {
    return DESIGNATE_BELOW_ONLY_MESSAGE;
  }
  return null;
};

export const employeeAssignmentError = ({
  accessType,
  managerId,
  departmentId,
  employees,
  currentEmployeeId,
}: {
  accessType: string
  managerId: string
  departmentId?: string
  employees: EmployeeRow[]
  currentEmployeeId?: string | null
}): string | null => {
  const currentId = String(currentEmployeeId || '').trim();
  if (isCeoAccessType(accessType)) {
    const otherCeo = employees.find((row) => (
      row.id !== currentId
      && isCeoAccessType(row.fields?.accessType)
    ));
    if (otherCeo) return 'Only one employee can be CEO';
  }
  const manager = String(managerId || '').trim();
  if (!manager) return null;
  const managerRow = employees.find((row) => row.id === manager);
  if (!managerRow) return 'Selected manager was not found';
  if (isOwnerEmployeeFields(managerRow.fields) && !isDevelopmentEnv()) {
    return 'Owner can only be selected as manager in development';
  }
  if (!employeeAccessActsAsManager(managerRow.fields)) {
    return 'Selected person is not a manager';
  }
  const dept = String(departmentId || '').trim();
  if (dept && !employeeIsManagerInDepartment(managerRow.fields, dept)) {
    return 'Selected manager must be in this department';
  }
  if (isCeoAccessType(managerRow.fields?.accessType)) {
    const already = employees.find((row) => (
      row.id !== currentId
      && String(row.fields?.manager || '') === manager
    ));
    if (already) return 'CEO can only be assigned as manager once';
  }
  return null;
};

/** @deprecated Use ACCESS_TYPE_CEO */
export const ACCESS_TYPE_CTO = LEGACY_ACCESS_TYPE_CTO;
